/**
 * Sonic Identity Art Styles Configuration
 * Shared across admin generate pages and playground
 */

// Art style IDs used for Sonic Identity generation
export type SonicArtStyleId = 'urban-skyline' | 'nature' | 'cosmic' | 'ocean' | 'abstract' | 'forest' | 'desert';

// Full art style definition with all properties
export interface SonicArtStyle {
  id: SonicArtStyleId;
  name: string;
  description: string;
  icon: string;
  keywords: string;
}

// Complete art style configurations for admin generation pages
export const SONIC_ART_STYLES: SonicArtStyle[] = [
  {
    id: 'urban-skyline',
    name: 'Urban Skyline',
    description: 'City buildings, skyscrapers, bridges, urban life',
    icon: '\u{1F3D9}\u{FE0F}', // 🏙️
    keywords: 'buildings, skyscrapers, bridges, cars, city life, urban landscape',
  },
  {
    id: 'nature',
    name: 'Nature',
    description: 'Trees, mountains, wildlife, natural landscapes',
    icon: '\u{1F33F}', // 🌿
    keywords: 'trees, mountains, birds, deer, flowers, leaves, natural elements',
  },
  {
    id: 'ocean',
    name: 'Ocean',
    description: 'Waves, sea creatures, maritime elements',
    icon: '\u{1F30A}', // 🌊
    keywords: 'waves, fish, whales, dolphins, coral, ships, lighthouse, beach',
  },
  {
    id: 'cosmic',
    name: 'Cosmic',
    description: 'Stars, planets, space elements, celestial bodies',
    icon: '\u{2728}', // ✨
    keywords: 'stars, planets, rockets, astronauts, galaxies, moons, constellations',
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Dense woodland, forest creatures, mystical trees',
    icon: '\u{1F332}', // 🌲
    keywords: 'trees, owls, foxes, mushrooms, ferns, woodland creatures, enchanted forest',
  },
  {
    id: 'desert',
    name: 'Desert',
    description: 'Dunes, camels, oasis, Arabian architecture',
    icon: '\u{1F3DC}\u{FE0F}', // 🏜️
    keywords: 'camels, dunes, palms, mosques, Arabian patterns, oasis, sand',
  },
  {
    id: 'abstract',
    name: 'Abstract',
    description: 'Geometric shapes, patterns, artistic elements',
    icon: '\u{1F3A8}', // 🎨
    keywords: 'geometric shapes, spirals, patterns, flowing lines, artistic elements',
  },
];

// Simplified art style for playground (id, name, icon only)
export const SONIC_ART_STYLES_SIMPLE = SONIC_ART_STYLES.map(({ id, name, icon }) => ({
  id,
  name,
  icon,
}));

// Helper to get art style by ID
export function getSonicArtStyle(id: SonicArtStyleId): SonicArtStyle | undefined {
  return SONIC_ART_STYLES.find(style => style.id === id);
}

// Valid art style IDs for validation
export const SONIC_ART_STYLE_IDS = SONIC_ART_STYLES.map(s => s.id) as SonicArtStyleId[];
